import { useEffect, useState } from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";

import { BuybackPeriod, StockBuybacks as StockBuybacksData, getStockBuybacks } from "@/controllers/buybacks";

/**
 * Executed share buybacks for one company, on the Holders tab.
 *
 * A company repurchasing its own stock is frequently the single largest buyer that stock has,
 * which is why this sits with the institutional holders rather than in a section of its own.
 *
 * **This is filed execution, not an announcement.** The Events tab carries announced
 * programmes; those are statements of intent and a large share are never fully executed. The
 * two disagreeing is normal and is the point of showing this one.
 *
 * **Renders nothing when there is no filed buyback.** Most companies have never repurchased
 * stock, and an empty panel on every one of them is worse than no panel — the same rule the
 * Alt Data tab follows.
 */

export const useStockBuybacks = (tradingitemId?: number) => {
    const [data, setData] = useState<StockBuybacksData | null>(null);
    const [loading, setLoading] = useState(Boolean(tradingitemId));

    useEffect(() => {
        if (!tradingitemId) {
            setData(null);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        getStockBuybacks(tradingitemId, controller.signal)
            .then((payload) => setData(payload))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [tradingitemId]);

    return { data, loading, hasData: Boolean(data?.hasData) };
};

/** Compact currency, e.g. `$23.6B`. Exported so the wording is testable. */
export const formatMoney = (value: number | null): string => {
    if (value === null || value === undefined) return "—";
    const formatted = Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Math.abs(value));
    return `${value < 0 ? "-" : ""}$${formatted}`;
};

/** Compact share count, e.g. `13.4M`. */
export const formatShares = (value: number | null): string => {
    if (value === null || value === undefined) return "—";
    return Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value);
};

/**
 * How stale the newest figure is, stated plainly.
 *
 * US buyback data is quarterly and filed with the 10-Q, so it is routinely two to three months
 * old. Saying so is better than letting a reader assume it is current — there is no daily US
 * buyback disclosure to be had, because the SEC rule requiring one was vacated in 2023.
 */
export const describeFreshness = (period: string | null, publishedAt: string | null): string => {
    if (!period) return "";
    const filed = publishedAt ? `, filed ${publishedAt.slice(0, 10)}` : "";
    return `Fiscal quarter ending ${period}${filed}.`;
};

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Box sx={{ minWidth: 140 }}>
        <Typography variant="caption" sx={{ display: "block" }}>
            {hint ? (
                <Tooltip title={hint}>
                    <span>{label}</span>
                </Tooltip>
            ) : (
                label
            )}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {value}
        </Typography>
    </Box>
);

const PeriodRow = ({ period }: { period: BuybackPeriod }) => (
    <Box component="tr" sx={{ "& td": { padding: "4px 12px 4px 0", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } }}>
        <td>{period.periodDate}</td>
        <td>{formatMoney(period.value)}</td>
        <td>{formatShares(period.shares)}</td>
        <td>{formatMoney(period.remainingAuthorization)}</td>
    </Box>
);

interface Props {
    tradingitemId?: number;
    /** Newest first, capped — the panel is a summary, not the full history. */
    maxPeriods?: number;
}

const StockBuybacks = ({ tradingitemId, maxPeriods = 8 }: Props) => {
    const { data, loading, hasData } = useStockBuybacks(tradingitemId);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!hasData || !data) {
        return (
            <Typography variant="body2" sx={{ opacity: 0.7, padding: "16px" }}>
                This company has not filed a share repurchase.
            </Typography>
        );
    }

    const periods = [...data.periods].reverse().slice(0, maxPeriods);
    const latest = periods[0];

    return (
        <Box sx={{ padding: "8px 16px 16px" }}>
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 2 }}>
                <Stat label="Bought back (4 qtrs)" value={formatMoney(data.trailingValue)} />
                <Stat label="Shares (4 qtrs)" value={formatShares(data.trailingShares)} />
                <Stat label="Latest quarter" value={formatMoney(data.latestValue)} />
                <Stat
                    label="Authorization left"
                    value={formatMoney(data.remainingAuthorization)}
                    hint="How much the company may still repurchase under its announced programmes: the dry powder. A large announced programme with nothing left authorized is finished, whatever the announcement said."
                />
            </Box>

            <Typography variant="caption" sx={{ display: "block", marginBottom: 1 }}>
                {describeFreshness(data.latestPeriod, latest?.publishedAt ?? null)} Filed with the company&apos;s 10-Q or 10-K; there is no daily US buyback disclosure.
            </Typography>

            <Box component="table" sx={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                    <Box component="tr" sx={{ "& th": { padding: "4px 12px 4px 0", textAlign: "left", opacity: 0.7 } }}>
                        <th>Quarter ending</th>
                        <th>Spend</th>
                        <th>Shares</th>
                        <th>Authorization left</th>
                    </Box>
                </thead>
                <tbody>
                    {periods.map((period) => (
                        <PeriodRow key={period.periodDate} period={period} />
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

export default StockBuybacks;
